import { ChatMessage, ChatSession, DurationColorRule, DurationColorRuleSeconds, UserRole } from '../types';
import { dateReviver } from '../utils/date';
import { UserHelper } from './UserHelper';

type TimerSessionSource = Pick<ChatSession, 'assign' | 'liveChatRedirect'> | null | undefined;

const toTimestamp = (value: string | Date | null | undefined): number | null => {
  if (!value) {
    return null;
  }
  const time = new Date(dateReviver(value)).getTime();
  return Number.isNaN(time) ? null : time;
};

export class TimerHelper {
  static DEFAULT_TIMER_COLOR_RULES: DurationColorRule[] = [
    { start: null, end: '00:30', color: 'default' },
    { start: '00:30', end: '00:45', color: 'warning' },
    { start: '00:45', end: null, color: 'error' }
  ];

  static MAX_UNREAD_TIMER_SECONDS = (59 * 60) + 99;

  static parseDurationToSeconds = (value: string | null): number | null => {
    if (!value) return null;
    const [minutes, seconds] = value.split(':').map(Number);
    if (Number.isNaN(minutes) || Number.isNaN(seconds)) return null;
    return (minutes * 60) + seconds;
  };

  static mapRulesToSeconds = (rules: DurationColorRule[]): DurationColorRuleSeconds[] => (
    rules.map(rule => ({
      start: this.parseDurationToSeconds(rule.start),
      end: this.parseDurationToSeconds(rule.end),
      color: rule.color
    }))
  );

  static formatElapsedTime = (seconds: number): string => {
    const maxDisplaySeconds = (59 * 60) + 59; // 59 minutes 59 seconds = 3599 seconds
    const clamped = Math.max(0, Math.min(seconds, maxDisplaySeconds));
    const mm = Math.floor(clamped / 60).toString().padStart(2, '0');
    const ss = (clamped % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  };

  static findTimerColor = (rules: DurationColorRuleSeconds[], elapsed: number): string => {
    const target = Math.max(0, elapsed);
    const match = rules.find(rule => {
      const startsAfter = rule.start === null || target >= rule.start;
      const endsBefore = rule.end === null || target < rule.end;
      return startsAfter && endsBefore;
    });
    // Map to Material UI Badge color values
    const colorMap: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      'warning': 'warning',
      'error': 'error',
      'info': 'info',
      'success': 'success',
      'primary': 'primary',
      'secondary': 'secondary',
      'default': 'default'
    };
    const color = match?.color || 'default';
    return colorMap[color.toLowerCase()] || 'default';
  };

  static areNumberMapsEqual = (a: Record<string, number>, b: Record<string, number>) => {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every(key => a[key] === b[key]);
  };

  static areNullableNumberMapsEqual = (a: Record<string, number | null>, b: Record<string, number | null>) => {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every(key => a[key] === b[key]);
  };

  /** True when the latest client message has no agent reply after it. */
  static hasPendingAgentResponse = (messages?: ChatMessage[]): boolean => {
    if (!messages?.length) {
      return false;
    }

    let lastClientIndex = -1;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].sender?.role === UserRole.Client) {
        lastClientIndex = i;
        break;
      }
    }

    if (lastClientIndex === -1) {
      return false;
    }

    for (let i = lastClientIndex + 1; i < messages.length; i += 1) {
      if (UserHelper.isAgentUserRole(messages[i].sender?.role)) {
        return false;
      }
    }

    return true;
  };

  /**
   * Unread-timer anchor: most recent unread client message, or queue redirect when
   * messages are not loaded yet (waiting sessions).
   */
  static resolveUnreadTimerStartMs = (
    session: TimerSessionSource,
    messages?: ChatMessage[]
  ): number | null => {
    if (messages?.length) {
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const message = messages[i];
        if (message.sender?.role !== UserRole.Client || message.read) {
          continue;
        }

        const clientMessageTime = toTimestamp(message.created);
        if (clientMessageTime !== null) {
          return clientMessageTime;
        }
        break;
      }

      return null;
    }

    return toTimestamp(session?.liveChatRedirect);
  };
}
